import { Box, Grid } from '@mui/material';
import { getPurchaseOrdersHistory, postPaymentSchedule } from 'api';
import { getQBAlerts } from 'api/adminHub';
import {
  deletePurchaseOrderItem,
  getAllPaymentSchedule,
  postPurchaseOrderItems,
  updatePurchaseOrder,
  updatePurchaseOrderItems
} from 'api/index';
import type {
  GetPurchaseOrders,
  PaymentPoDetails,
  PaymentSchedule,
  PaymentScheduleForm,
  PaymentSchedulesItems,
  PurchaseOrderBills,
  PurchaseOrderItem
} from 'api/types';
import type { DropdownOptionType } from 'components/inputs/Dropdown';
import CustomButton from 'components/NewLayout/Button';
import PaperContainer from 'components/PaperContainer';
import UploadPurchaseOrderBlob from 'components/UploadPurchaseOrderBlob';
import EditPoModal from 'pages/ProcurementWorkspace/components/EditPoModal';
import PaymentScheduleModal from 'pages/ProcurementWorkspace/components/PaymentScheduleModal';
import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useLocation } from 'react-router-dom';
import { useAdminWorkspaceStore } from 'store/index';
import { defaultPageSize } from 'utils/constants';
import {
  calculateItemsTotal,
  calculateModifiersTotal,
  getPrice,
  getS3Path,
  tabTitle
} from 'utils/index';
import { v4 as uuidv4 } from 'uuid';
import PaymentScheduleTable from './PaymentScheduleTable';
import UpdatePaymentScheduleModal from './UpdatePaymentScheduleModal';

export default function PaymentHistory({
  allProjects,
  searchText
}: {
  allProjects: DropdownOptionType[];
  searchText: string;
}) {
  tabTitle('Payment Workspace');
  const { state } = useLocation();
  const [showCreatePayment, setShowCreatePayment] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [paymentSchedule, setPaymentSchedule] = useState<PaymentSchedule[]>([]);
  const setQBAlerts = useAdminWorkspaceStore((state) => state.setQBAlerts);
  const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState<GetPurchaseOrders | null>();
  const [isUpdatePoModalVisible, setUpdatePoModalVisibility] = useState<boolean>(false);
  const [isUpdateScheduleModalAfterPOVisible, setUpdateScheduleModalAfterPOVisibility] =
    useState<boolean>(false);
  const [isUpdateScheduleModalVisible, setUpdateScheduleModalVisibility] = useState<boolean>(false);
  const [purchaseOrderItemsToBeDeletedList, setPurchaseOrderItemsToBeDeletedList] =
    useState<string[]>();
  const [newRows, setNewRows] = useState<PurchaseOrderItem[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [paginationData, setPaginationData] = useState<{
    pageNumber: string;
    pageSize: string;
    searchText: string;
  }>({ pageNumber: '1', pageSize: defaultPageSize, searchText });

  useEffect(() => {
    getQBAlerts().then((res) => setQBAlerts(res ?? []));
  }, []);

  // Use ref to track the latest search value and prevent race conditions
  const latestSearchRef = useRef(searchText);
  const fetchTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    latestSearchRef.current = searchText || '';

    setPaginationData((prev) => ({
      ...prev,
      pageNumber: searchText !== prev.searchText ? '1' : prev.pageNumber,
      searchText: searchText || '' // Ensure it's never undefined
    }));
  }, [searchText]);

  // Fetch payments with debouncing to prevent rapid API calls
  useEffect(() => {
    // Clear any existing timeout
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }

    // Add a small delay to prevent rapid API calls
    fetchTimeoutRef.current = setTimeout(() => {
      fetchAllPayments();
    }, 100);

    // Cleanup timeout on unmount
    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [paginationData, state?.purchase_order_number]);

  const fetchAllPayments = async () => {
    // Prevent multiple simultaneous calls
    if (isLoading) return;

    setIsLoading(true);
    try {
      const { pageNumber, pageSize, searchText: paginationSearchText } = paginationData;

      if (!pageNumber || !pageSize) {
        console.warn('Missing pagination data', paginationData);
        return;
      }

      // Use the latest search text from ref to avoid stale closure
      const currentSearchText = latestSearchRef.current;
      const filterValue =
        currentSearchText && currentSearchText.trim()
          ? currentSearchText.trim()
          : state?.purchase_order_number;

      const getPaymentSchedule = await getAllPaymentSchedule(
        String(pageNumber),
        String(pageSize),
        filterValue || undefined,
        'paymentHistory'
      );

      // Check if this is still the latest search (prevent race conditions)
      if (latestSearchRef.current !== currentSearchText) {
        console.log('Search text changed during API call, ignoring results');
        return;
      }

      const filteredPayments =
        currentSearchText?.trim() || !state?.purchase_order_id
          ? getPaymentSchedule.data
          : getPaymentSchedule.data.filter(
              (res: PaymentSchedulesItems) => res.purchase_order_id === state.purchase_order_id
            );

      const allPayments = filteredPayments.map((p) => ({
        ...p,
        subRows: p.paymentschedules
      }));

      setPaymentSchedule(allPayments);
      setTotalCount(getPaymentSchedule.totalCount);
    } catch (error) {
      console.error('Error fetching payments:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const onClickScheduleEdit = (row) => {
    setSelectedPurchaseOrder(row);
    setUpdateScheduleModalAfterPOVisibility(true);
  };

  const handlePurchaseOrderNumberClick = (row: GetPurchaseOrders) => {
    setSelectedPurchaseOrder(row);
    setPurchaseOrderItemsToBeDeletedList([]);
    setUpdatePoModalVisibility(true);
  };

  const deletePurchaseOrderItem_ = async (purchaseOrderItemId: string) => {
    setPurchaseOrderItemsToBeDeletedList((prev = []) => [...prev, purchaseOrderItemId]);

    if (selectedPurchaseOrder) {
      const updatedPurchaseOrder = {
        ...selectedPurchaseOrder,
        purchase_order_items: selectedPurchaseOrder?.purchase_order_items?.filter(
          (item) => item.purchase_order_item_id !== purchaseOrderItemId
        )
      };

      setSelectedPurchaseOrder(updatedPurchaseOrder);
    }
  };

  const paymentPoDetails = () => {
    const newRowsTotal = calculateItemsTotal(newRows);
    const existingPurchaseOrderItemsTotal = calculateItemsTotal(
      selectedPurchaseOrder?.purchase_order_items
    );
    const total = Number(newRowsTotal) + Number(existingPurchaseOrderItemsTotal);

    const { shipping_cost, estimated_taxes, discount, deposit, refund } =
      selectedPurchaseOrder ?? {};

    const poCalcualtedTotal: number =
      total +
      parseFloat(shipping_cost || '0') +
      parseFloat(estimated_taxes ?? '0') -
      parseFloat(discount ?? '0') -
      parseFloat(deposit ?? '0') -
      parseFloat(refund ?? '0');

    const purchaseOrderData: PaymentPoDetails = {
      total_amount: poCalcualtedTotal,
      paid_amount: getPrice(selectedPurchaseOrder, 'total'),
      remaining_amount: poCalcualtedTotal - getPrice(selectedPurchaseOrder, 'total'),
      scheduled_amount: getPrice(selectedPurchaseOrder),
      po_number: selectedPurchaseOrder?.purchase_order_number || '',
      vendor_name: selectedPurchaseOrder?.vendor?.vendor_name || '',
      po_id: selectedPurchaseOrder?.purchase_order_id || ''
    };
    return purchaseOrderData;
  };

  const updatePurchaseOrder_ = async () => {
    const prevPaymentScheduleState = [...paymentSchedule];
    const systemGeneratedPdFileName = `system-generated-pdf-${uuidv4()}.pdf`;
    try {
      setIsLoading(true);
      if (selectedPurchaseOrder) {
        const getPaymentSchedule = await getAllPaymentSchedule(
          String(1),
          String(1),
          selectedPurchaseOrder?.purchase_order_number
        );

        if (newRows.length) {
          const newPurchaseOrderItems_: Partial<PurchaseOrderItem>[] = newRows.map((item) => ({
            item_name: item.item_name,
            quantity: item.quantity,
            unit_of_measure: item.unit_of_measure,
            price: item.price,
            urgent_order_status_id: 2,
            project_id: item.project_id,
            description: item.description,
            suggested_vendor: '',
            purchase_order_id: selectedPurchaseOrder?.purchase_order_id
          }));

          await postPurchaseOrderItems(newPurchaseOrderItems_);
        }

        const existingItemsTotal = selectedPurchaseOrder?.purchase_order_items
          ? calculateItemsTotal(selectedPurchaseOrder.purchase_order_items)
          : 0;

        const newItemsTotal = newRows.reduce(
          (total, item) => total + Number(item.price) * Number(item.quantity),
          0
        );

        const modifierDetails = {
          shipping_cost: selectedPurchaseOrder?.shipping_cost,
          estimated_taxes: selectedPurchaseOrder?.estimated_taxes,
          discount: selectedPurchaseOrder?.discount,
          deposit: selectedPurchaseOrder?.deposit,
          refund: selectedPurchaseOrder?.refund
        };

        const modifiersTotal = calculateModifiersTotal(modifierDetails);
        const total_price = (
          Number(existingItemsTotal) +
          Number(modifiersTotal) +
          Number(newItemsTotal)
        ).toFixed(2);

        const purchaseOrderToUpdate: GetPurchaseOrders = {
          ...selectedPurchaseOrder,
          paymentschedules: getPaymentSchedule.data[0].paymentschedules,
          total_price,
          system_generated_pdf_s3_uri: getS3Path(
            systemGeneratedPdFileName,
            'system-generated-purchase-orders'
          )
        };

        purchaseOrderItemsToBeDeletedList?.length &&
          (await deletePurchaseOrderItem(purchaseOrderItemsToBeDeletedList));

        selectedPurchaseOrder?.purchase_order_items &&
          (await updatePurchaseOrderItems(selectedPurchaseOrder.purchase_order_items));

        await updatePurchaseOrder(purchaseOrderToUpdate, selectedPurchaseOrder.purchase_order_id);

        const fetchUpdatedPurchaseOrder_ = await await getPurchaseOrdersHistory(
          '1',
          defaultPageSize,
          selectedPurchaseOrder.purchase_order_id
        );

        const updatedPurchaseOrder = fetchUpdatedPurchaseOrder_.data[0];

        await UploadPurchaseOrderBlob({
          data: updatedPurchaseOrder.purchase_order_items,
          vendorBillingData: updatedPurchaseOrder.vendor,
          deliveryAddress: updatedPurchaseOrder.delivery_address,
          poModifiers: {
            shipping_cost: updatedPurchaseOrder.shipping_cost,
            estimated_taxes: updatedPurchaseOrder.estimated_taxes,
            discount: updatedPurchaseOrder.discount,
            deposit: updatedPurchaseOrder.deposit,
            refund: updatedPurchaseOrder.refund
          },
          specialInstructions: updatedPurchaseOrder.instructions || '',
          poResponse: {
            purchase_order_id: updatedPurchaseOrder.purchase_order_id,
            purchase_order_number: updatedPurchaseOrder.purchase_order_number
          },
          requester: updatedPurchaseOrder.requester,
          poDate: updatedPurchaseOrder.created_at,
          fileName: systemGeneratedPdFileName,
          paymentTerms: updatedPurchaseOrder.payment_terms
        });
      }
    } catch (error) {
      console.error('Error adding purchase order item:', error);
      setPaymentSchedule(prevPaymentScheduleState);
    } finally {
      setIsLoading(false);
    }
    setUpdatePoModalVisibility(false);
  };

  const updatePaymentSchedule_ = async (paymentSchedule: PaymentScheduleForm[]) => {
    try {
      const purchaseOrderBills: PurchaseOrderBills = {
        paymentSchedules: paymentSchedule
      };

      await postPaymentSchedule(purchaseOrderBills, String(paymentPoDetails().po_id ?? ''));
    } catch (error) {
      toast.error('Error during bill update or deletion: ' + error, {
        position: 'top-center'
      });
    }
  };

  const onSubmitPurchaseOrderAndPaymentSchedule = async (
    paymentSchedule: PaymentScheduleForm[]
  ) => {
    try {
      setIsLoading(true);

      if (selectedPurchaseOrder) {
        await updatePurchaseOrder_();
        await updatePaymentSchedule_(paymentSchedule);
        await fetchAllPayments();
      }

      toast.success('Bills updated successfully!', { position: 'top-center' });
    } catch (error) {
      console.error('Error updating bills:', error);
      toast.error('Failed to update bills. Please try again.', { position: 'top-center' });
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  const onSubmitPaymentSchedule = async (paymentSchedule: PaymentScheduleForm[]) => {
    try {
      setIsLoading(true);

      if (selectedPurchaseOrder) {
        await updatePaymentSchedule_(paymentSchedule);
        await fetchAllPayments();
      }

      toast.success('Bills updated successfully!', { position: 'top-center' });
    } catch (error) {
      console.error('Error updating bills:', error);
      toast.error('Failed to update bills. Please try again.', { position: 'top-center' });
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  const onBackToEditPo = () => {
    setUpdateScheduleModalAfterPOVisibility(false);
    setUpdatePoModalVisibility(true);
  };

  const onClose = () => {
    setNewRows([]);
    setSelectedPurchaseOrder(null);
    setUpdatePoModalVisibility(false);
    setUpdateScheduleModalVisibility(false);
    setUpdateScheduleModalAfterPOVisibility(false);
  };

  return (
    <PaperContainer>
      <Grid container spacing={2} justifyContent="flex-end">
        <Grid item xs={12} sm={8}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 1 }}>
            {state?.purchase_order_id && paymentSchedule.length === 0 && (
              <CustomButton
                onClick={() => setShowCreatePayment(true)}
                className="admin-workspace-btn admin-workspace-create-btn"
                label={`Create Payment`}
              />
            )}
          </Box>
        </Grid>
      </Grid>
      <br />
      <PaymentScheduleTable
        loading={isLoading}
        paymentSchedule={paymentSchedule}
        fetchAllPayments={fetchAllPayments}
        totalCount={totalCount}
        setPaginationData={setPaginationData}
        onClickPurchaseOrderNumber={handlePurchaseOrderNumberClick}
        onClickScheduleEdit={onClickScheduleEdit}
        parentComponent="PaymentHistory"
      />

      {showCreatePayment && (
        <PaymentScheduleModal
          purchaseOrderId={state?.purchase_order_id}
          selectedPoTotalPrice={
            state?.subRows?.reduce((sum, item) => sum + Number(item.price), 0) ?? 0
          }
          selectedVendorPayment={''}
          poModifiers={{
            estimated_taxes: state?.estimated_taxes,
            shipping_cost: state?.shipping_cost,
            discount: state?.discount,
            deposit: state?.deposit,
            refund: state?.refund
          }}
          onClose={() => setShowCreatePayment(false)}
          onComplete={() => fetchAllPayments()}
        />
      )}

      {isUpdatePoModalVisible && selectedPurchaseOrder && (
        <EditPoModal
          isOpen={isUpdatePoModalVisible}
          deletePurchaseOrderItem={deletePurchaseOrderItem_}
          purchaseOrder={selectedPurchaseOrder}
          setSelectedPurchaseOrder={setSelectedPurchaseOrder}
          onClose={onClose}
          allProjects={allProjects}
          newRows={newRows}
          setNewRows={setNewRows}
          onContinue={(purchaseOrderToUpdate) => {
            setUpdatePoModalVisibility(false);
            setUpdateScheduleModalAfterPOVisibility(true);
            setSelectedPurchaseOrder(purchaseOrderToUpdate);
          }}
        />
      )}

      {isUpdateScheduleModalAfterPOVisible && selectedPurchaseOrder && (
        <UpdatePaymentScheduleModal
          paymentPoDetails={paymentPoDetails()}
          purchaseOrder={selectedPurchaseOrder}
          onClose={() => onClose()}
          onBackToEditPo={onBackToEditPo}
          onComplete={(paymentSchedule: PaymentScheduleForm[]) =>
            onSubmitPurchaseOrderAndPaymentSchedule(paymentSchedule)
          }
        />
      )}

      {isUpdateScheduleModalVisible && selectedPurchaseOrder && (
        <UpdatePaymentScheduleModal
          paymentPoDetails={paymentPoDetails()}
          purchaseOrder={selectedPurchaseOrder}
          onClose={() => onClose()}
          onComplete={(paymentSchedule: PaymentScheduleForm[]) =>
            onSubmitPaymentSchedule(paymentSchedule)
          }
        />
      )}
    </PaperContainer>
  );
}
